/**
 * Manage cookie creation, unique ID and updation for tracking.
 *
 * @type {Object}
 */

import Utils from './utils';
import SharedStorage from './shared-storage';
import NADA from './nada';
import RETAIL360 from './retail360';

/**
 * GlobalCookie
 */
const GlobalCookie = {

	/**
   * maintain cookie synced state.
   */
	isSynced: false,

	/**
   * Initialize
   *
   * @returns void
   */
	init() {

		/**
		 * Unique shared storage key to read and write the data between sites.
		 */
		this.storageKey = 'coxamt';

		// Create shared storage connection.
		this.storage = new SharedStorage();

		if ( ! this.storage.options ) {
			return;
		}

		// Cookie key.
		this.cookieName = 'coxmkt';

		this.domain = this.storage.options.domain;

		this.emailAliases = [
			'email',
			'emailAddress',
			'Email',
			'email-address',
			'e'
		];

		this.emailLabelAliases = 'Email, Email Address';

		this.gForm = this.gravityForm();

		window.addEventListener( 'load', () => {
			NADA.init();
			RETAIL360.init();
			this.sync();
			this.gForm.updateEmailOnSubmit();
			this.updateHashedEmail();

			// Wait until google analytics `_ga` cookie stored in the cookie.
			Utils.waitUntil( () => {
				let cookie = this.get( false );

				return ( '' !== this.getGoogleAnalyticsClientID() && 'object' === typeof cookie && null !== cookie );
			} ).then( () => {

				// Once google analytics `_ga` cookie stored then execute the below task.
				this.updateGACID();
			} );
		} );
	},

	/**
	 * Sync the cookie with hosts' local storage.
	 */
	sync() {

		/**
		 * Create or update the cookie.
		 */
		const storeCookie = () => {
			let localCookie = this.get( false );

			// If cookie is unavailable the cookie then create one.
			if ( ! localCookie ) {
				localCookie = this.generateCookie();
			}

			if ( ! localCookie ) {
				return;
			}

			// Update the cookie and local storage data.
			this.update( Utils.serialize( localCookie ) );
		};

		/**
		 * Sync cookie with local storage data.
		 *
		 * @param {*} cookie
		 * @returns
		 */
		const syncCookie = ( cookie ) => {

			if ( ! cookie || 'undefined' === cookie || 'undefined' === typeof cookie ) {
				return null;
			}

			if ( ! cookie.hasOwnProperty( 'gwid' ) ) {
				return null;
			}

			// Generate cookie for current site to grab the email address and gacid.
			let currentSiteCookie = this.generateCookie();

			if ( currentSiteCookie ) {
				if ( ! cookie.gacid.hasOwnProperty( this.domain ) ) {
					cookie.gacid[this.domain] = '';
				}

				// Merge current site gacid with local storage data.
				if ( currentSiteCookie.gacid.hasOwnProperty( this.domain ) && '' !== currentSiteCookie.gacid[this.domain] ) {
					cookie.gacid[this.domain] = currentSiteCookie.gacid[this.domain];
				}

				// Update the email address if found from current site cookie.
				// Update email, even we have alias of email present in URL string.
				// Do not check what is currently stored, just update it.
				if ( currentSiteCookie.emal && '' !== currentSiteCookie.emal ) {
					cookie.emal = currentSiteCookie.emal;
				}

				// Update the caid if found from current site cookie.
				if ( currentSiteCookie.caid && '' !== currentSiteCookie.caid ) {
					cookie.caid = currentSiteCookie.caid;
				}

				// Update the batch_id if found from current site cookie.
				if ( currentSiteCookie.batch_id && '' !== currentSiteCookie.batch_id ) {
					// eslint-disable-line camelcase
					cookie.batch_id = currentSiteCookie.batch_id; // eslint-disable-line camelcase
				}

				// Update the job_id if found from current site cookie.
				if ( currentSiteCookie.job_id && '' !== currentSiteCookie.job_id ) {
					// eslint-disable-line camelcase
					cookie.job_id = currentSiteCookie.job_id; // eslint-disable-line camelcase
				}

				// Update the cookie and local storage data.
				this.update( Utils.serialize( cookie ) );
			}

			return cookie;
		};

		// Check for the local storage data.
		this.storage.get( this.storageKey, ( error, data ) => {

			if ( error || 'undefined' === typeof data || ! data || null === data ) {
				storeCookie();

				/**
				 * Not using `gform_post_render` hook by gravity form
				 * as execution of JavaScript code is async and sync of the tracking data takes time to populate
				 * and `gform_post_render` hook get executed in async manner.
				 */
				this.gForm.populate();
				this.displayCookieOnFrontend();
				this.updateFpcButtonUrl();

				// Update value if cookie is synced.
				this.isSynced = true;

				NADA.init();
				RETAIL360.init();

				return;
			}

			// If data exist then unserialize it.
			data = Utils.unSerialize( data );

			let synced = syncCookie( data );

			// If sync is failed then create the cookie and store in local storage.
			if ( ! synced ) {
				storeCookie();
			}

			/**
			 * Not using `gform_post_render` hook by gravity form
			 * as execution of JavaScript code is async and sync of the tracking data takes time to populate
			 * and `gform_post_render` hook get executed in async manner.
			 */
			this.gForm.populate();
			this.displayCookieOnFrontend();
			this.updateFpcButtonUrl();

			// Update value if cookie is synced.
			this.isSynced = true;

			NADA.init();
			RETAIL360.init();
		} );
	},

	/**
	 * Create a serialized data for global-web-id cookie to be used by user.
	 *
	 * @returns serialized data after combining the data into JSON input.
	 */
	generateCookie() {
		let globalWebID = Utils.sanitize(
			globalWebID ? globalWebID : this.generateGlobalWebID()
		);
		let googleAnalyticsClientID = Utils.sanitize(
			googleAnalyticsClientID ?
				googleAnalyticsClientID :
				this.getGoogleAnalyticsClientID()
		);

		if ( 'undefined' === typeof googleAnalyticsClientID ) {
			googleAnalyticsClientID = '';
		}

		let email = Utils.sanitize( email ? email : this.extractEmailFromURL() );

		if ( 'undefined' !== typeof email && email ) {
			email = Utils.decodeHTMLEntities( email );
		}

		let caid = Utils.sanitize(
			caid ? caid : Utils.decodeHTMLEntities( Utils.extractValueFromURL( 'caid' ) )
		);
		let batchID = Utils.sanitize(
			batchID ?
				batchID :
				Utils.decodeHTMLEntities( Utils.extractValueFromURL( 'batch_id' ) )
		);
		let jobID = Utils.sanitize(
			jobID ?
				jobID :
				Utils.decodeHTMLEntities( Utils.extractValueFromURL( 'job_id' ) )
		);

		let cookieData = {
			gwid: globalWebID,
			gacid: {},
			emal: email,
			emalHash: email,
			gFormData: {},
			caid: caid,
			// eslint-disable-next-line camelcase
			batch_id: batchID,
			// eslint-disable-next-line camelcase
			job_id: jobID
		};

		cookieData.gacid[this.domain] = googleAnalyticsClientID;

		return cookieData;
	},

	/**
	 * Function to generate random Web-ID based on timestamp to be used to identify users.
	 * @returns Integer
	 */
	generateGlobalWebID() {
		let d = new Date().getTime();
		let d2 = ( 'undefined' !== typeof performance && performance.now && performance.now() * 1000 ) || 0; //Time in microseconds since page-load or 0 if unsupported
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
			/[xy]/g,
			( c ) => {
				let r = Math.random() * 16; //random number between 0 and 16
				if ( 0 < d ) {

					//Use timestamp until depleted
					r = ( d + r ) % 16 | 0;
					d = Math.floor( d / 16 );
				} else {

					//Use microseconds since page-load if supported
					r = ( d2 + r ) % 16 | 0;
					d2 = Math.floor( d2 / 16 );
				}
				return ( 'x' === c ? r : ( r & 0x3 ) | 0x8 ).toString( 16 );
			}
		);
	},

	/**
	 * Extract GA client ID from GA cookie present in the site.
	 *
	 * @returns Integer
	 */
	getGoogleAnalyticsClientID() {
		let gaCookie = Utils.getCookie( '_ga' );

		if ( ! gaCookie ) {
			return '';
		}

		let gaCookieValues = gaCookie.split( '.' );

		if ( 'undefined' !== typeof gaCookieValues[2] && 'undefined' !== typeof gaCookieValues[3] ) {
			return gaCookieValues[2] + '.' + gaCookieValues[3];
		}

		return '';
	},

	/**
	 * Extract GA client ID from GA cookie present in the site.
	 *
	 * @returns Integer
	 */
	extractEmailFromURL() {
		let currentPage = window.location;
		let url = new URL( currentPage );

		for ( let i = 0; i < this.emailAliases.length; i++ ) {
			let capturedEmail = url.searchParams.get( this.emailAliases[i] );
			if ( null !== capturedEmail ) {
				return capturedEmail;
			}
		}
		return '';
	},

	/**
	 * Get cookie.
	 *
	 * @param {*} serialized
	 * @returns
	 */
	get( serialized = true ) {
		let cookie = null;

		cookie = Utils.getCookie( this.cookieName );

		if ( ! cookie ) {
			return null;
		}

		return true === serialized ? cookie : Utils.unSerialize( cookie );
	},

	/**
	 * Update the cookie and local storage data.
	 *
	 * @param {*} data
	 */
	update( data ) {
		Utils.createCookie( this.cookieName, data, 365 );

		// Update record in storage as well.
		this.storage.set( this.storageKey, data, ( error, value ) => {} );
	},

	/**
	 * Update GACID by fetching latest _ga ID from the cookie.
	 */
	updateGACID() {
		let cookie = this.get( false );

		cookie.gacid[this.domain] = this.getGoogleAnalyticsClientID();

		this.update( Utils.serialize( cookie ) );
	},

	/**
	 * Update email address in cookie.
	 *
	 * @param {*} email
	 * @returns
	 */
	updateEmailInCookie( email ) {
		if ( ! Utils.validateEmail( email ) ) {
			return;
		}

		let cookie = this.get( false );

		if ( ! cookie ) {
			return;
		}

		/**
		 * Updates the 'emalHash' field of the cookie with a SHA-256 hash of the user's email.
		 *
		 * This function first hashes the provided email using the `Utils.hashEmail` method.
		 * Once the email is hashed, it retrieves the latest cookie data using `this.get(false)`,
		 * updates the 'emalHash' field with the hashed email, and then serializes and updates the
		 * cookie with the new data using `this.update`.
		 *
		 */
		Utils.hashEmail( email ).then( ( hashedEmail ) => {

			// Retrieve the latest state of the cookie data to ensure any updates are not overwritten.
			let latestCookie = this.get( false );

			// Update the 'emalHash' field with the newly SHA-256 hashed email.
			latestCookie.emalHash = hashedEmail;

			// AES Encrypt the Email.
			latestCookie.emal = Utils.encrypt( email );

			// Serialize and update the cookie with the new data.
			this.update( Utils.serialize( latestCookie ) );
		} );
	},

	/**
	 * Update GForms Fields in Cookie.
	 *
	 * @param {*} data
	 * @param {*} label
	 *
	 * @returns
	 */
	updateGFormsFieldsInCookie( data, label ) {
		let cookie = this.get( false );

		if ( ! cookie ) {
			return;
		}

		label = label.replace( /\s/g, '' ).toLowerCase(); // Remove spaces.

		if ( 'undefined' === typeof cookie.gFormData ) {
			cookie.gFormData = {};
		}

		cookie.gFormData[label] = data;

		this.update( Utils.serialize( cookie ) );
	},

	/**
	 * Update the gravity form data points value.
	 * Data points may have - Email, FirstName, LastName, Dealership, City, State, Zip, Phone etc.
	 *
	 * @returns
	 */
	gravityForm() {
		const populate = () => {
			let cookie = this.get( false );

			if ( ! cookie ) {
				return;
			}

			document.querySelectorAll( '.gform_wrapper' ).forEach( ( formElement ) => {
				let emailField = Utils.getGravityFormFieldByLabel( this.emailLabelAliases, formElement );

				// Create a new event
				let customEvent = new CustomEvent( 'postPopulateGformEmailWithCookies', {
					bubbles: true
				} );

				if ( emailField ) {
					Utils.decrypt(
						cookie.emal,
						( data ) => {
							if ( data ) {
								emailField.querySelector( 'input' ).value = data;

								// Dispatch the event
								emailField.dispatchEvent( customEvent );
							}
						},
						true
					);
				}

				// Update the Fields like FirstName, LastName, Phone, Zip etc.
				let formTextFields = Utils.getGformFieldsKeyLabelTextFields();

				Object.keys( formTextFields ).forEach( ( key ) => {
					let field = Utils.getGravityFormFieldByLabel(
						formTextFields[key],
						formElement
					);

					if ( field && cookie.gFormData && cookie.gFormData[key] ) {
						const input = field.querySelector( 'input' );
						const select = field.querySelector( 'select' );

						if ( input ) {
							if ( 'checkbox' === input.type ) {

								// Handle checkboxes: check if value is true, uncheck if empty
								input.checked = 'true' === cookie.gFormData[key];
							} else {

								// Handle other input types
								input.value = cookie.gFormData[key];
							}
						}

						if ( select ) {
							select.value = cookie.gFormData[key];
						}
					}
				} );

				let formFieldsClassNames = Utils.getGformFieldsKeyLabelByClassNameTextFields();

				Object.keys( formFieldsClassNames ).forEach( ( key ) => {
					let field = Utils.getGravityFormFieldByClassName(
						formFieldsClassNames[key],
						formElement
					);

					if ( field && cookie.gFormData && cookie.gFormData[key] ) {

						const inputField = field.querySelector( 'input' );
						const selectField = field.querySelector( 'select' );

						if ( inputField ) {
							if ( 'checkbox' === inputField.type ) {

								// Handle checkboxes: check if value is true, uncheck if empty
								inputField.checked = 'true' === cookie.gFormData[key];
							} else {

								// Handle other input types
								inputField.value = cookie.gFormData[key];
							}
						}

						if ( selectField ) {
							selectField.value = cookie.gFormData[key];
						}
					}
				} );

				let gacidField = Utils.getGravityFormFieldByClassName(
					'gacid',
					formElement
				);

				if ( gacidField && this.domain in cookie.gacid ) {
					gacidField.querySelector( 'input' ).value = cookie.gacid[this.domain];
				}

				let gwid = Utils.getGravityFormFieldByClassName( 'gwid', formElement );

				if ( gwid ) {
					gwid.querySelector( 'input' ).value = cookie.gwid;
				}

				let caid = Utils.getGravityFormFieldByClassName( 'caid', formElement );

				if ( caid ) {
					caid.querySelector( 'input' ).value = cookie.caid;
				}

				let batchID = Utils.getGravityFormFieldByClassName(
					'batch_id',
					formElement
				);

				if ( batchID ) {
					batchID.querySelector( 'input' ).value = cookie.batch_id;
				}

				let jobID = Utils.getGravityFormFieldByClassName( 'job_id', formElement );

				if ( jobID ) {
					jobID.querySelector( 'input' ).value = cookie.job_id;
				}
			} );
		};

		const updateEmailOnSubmit = () => {
			document.querySelectorAll( '.gform_wrapper' ).forEach( ( formElement ) => {
				let button = formElement.querySelector( '.gform_button' );

				button.addEventListener( 'click', ( event ) => {
					let eventListenerThis = this;
					let emailField = Utils.getGravityFormFieldByLabel(
						this.emailLabelAliases,
						formElement
					);

					if ( ! emailField ) {
						return;
					}

					let email = emailField.querySelector( 'input' ).value;

					// Update the email in cookie and in storage.
					this.updateEmailInCookie( email );

					// Update Multiple Form Fields in Cookie.

					// Get Text Fields.
					let formTextFields = Utils.getGformFieldsKeyLabelTextFields();

					Object.keys( formTextFields ).forEach( ( key ) => {	
						let field = Utils.getGravityFormFieldByLabel(
							formTextFields[key],
							formElement
						);

						if ( field ) {

							const inputField = field.querySelector( 'input' );
							if ( inputField ) {
								let fieldData = '';

								if ( 'checkbox' === inputField.type ) {

									// Handle checkbox: Store `true` if checked, `false` if unchecked
									fieldData = inputField.checked ? 'true' : 'false';
								} else {

									// Handle text input values
									fieldData = inputField.value;
								}

								eventListenerThis.updateGFormsFieldsInCookie( fieldData, key );
							}

							const selectField = field.querySelector( 'select' );

							if ( selectField ) {
								let fieldData = selectField.value;
								eventListenerThis.updateGFormsFieldsInCookie( fieldData, key );
							}
						}

						//  else {
						// 	eventListenerThis.updateGFormsFieldsInCookie( '', key );
						// }
					} );

					let formFieldsClassNames = Utils.getGformFieldsKeyLabelByClassNameTextFields();

					Object.keys( formFieldsClassNames ).forEach( ( key ) => {
						let field = Utils.getGravityFormFieldByClassName(
							formFieldsClassNames[key],
							formElement
						);

						if ( field ) {
							const inputField = field.querySelector( 'input' );
							if ( inputField ) {
								let fieldData = '';

								if ( 'checkbox' === inputField.type ) {

									// Handle checkbox: Store `true` if checked, `false` if unchecked
									fieldData = inputField.checked ? 'true' : 'false';
								} else {

									// Handle text input values
									fieldData = inputField.value;
								}

								eventListenerThis.updateGFormsFieldsInCookie( fieldData, key );
							}

							const selectField = field.querySelector( 'select' );
							if ( selectField ) {
								let fieldData = selectField.value;
								eventListenerThis.updateGFormsFieldsInCookie( fieldData, key );
							}
						} 

						// else {
						// 	eventListenerThis.updateGFormsFieldsInCookie( '', key );
						// }
					} );
				} );
			} );
		};

		return {
			populate,
			updateEmailOnSubmit
		};
	},

	/**
	 * Updates the hashed version of the email (`emalHash`) in the cookie if it exists
	 * but is not yet hashed. This function retrieves the cookie, checks if the `emal` (email)
	 * field is present and the `emalHash` field is either missing or empty, and if so,
	 * decrypts the email, hashes it using a SHA-256 algorithm (or equivalent), and updates
	 * the cookie with the newly generated hash.
	 *
	 * Process:
	 * 1. Retrieves the current cookie.
	 * 2. Checks if the `emal` field exists and the `emalHash` is missing or empty.
	 * 3. If conditions are met, decrypts the email using `Utils.decrypt`.
	 * 4. Hashes the decrypted email using `Utils.hashEmail`.
	 * 5. Updates the cookie with the generated `emalHash`.
	 * 6. Logs any errors during the decryption or hashing processes.
	 *
	 * Conditions:
	 * - The email (`emal`) must exist and be non-empty.
	 * - The `emalHash` must either be missing or an empty string.
	 *
	 * @returns {void} - The function doesn't return a value. It modifies the cookie in place.
	 */
	updateHashedEmail() {

		// Retrieve the current cookie.
		let cookie = this.get( false );

		// If no cookie exists, exit the function.
		if ( ! cookie ) {
			return;
		}

		// Check if the email exists and if the email hash is either missing or not present.
		if ( cookie.emal && ( ! cookie.emalHash || '' === cookie.emalHash ) ) {

			// Attempt to decrypt the email.
			Utils.decrypt(
				cookie.emal,
				( decryptedEmail ) => {
					if ( decryptedEmail ) {

						// Hash the decrypted email.
						Utils.hashEmail( decryptedEmail ).then( ( hashedEmail ) => {

							// Retrieve the latest state of the cookie data to ensure any updates are not overwritten.
							let latestCookie = this.get( false );

							// Update the 'emalHash' field with the newly SHA-256 hashed email.
							latestCookie.emalHash = hashedEmail;

							// Serialize and update the cookie.
							this.update( Utils.serialize( latestCookie ) );
						} ).catch( ( error ) => {

							// Handle any errors during the hashing process
							console.error( 'Error hashing the email:', error );
						} );
					} else {
						console.error( 'Failed to decrypt the email.' );
					}
				},
				true
			);

		}
	},


	/**
	 * Displays user information from the 'coxmkt' cookie on the frontend.
	 *
	 * This function retrieves the 'coxmkt' cookie, checks for the presence of
	 * 'gFormData' and 'emal' fields, and then dynamically creates and displays
	 * user information in a table format within the '.user-fpc-info' container.
	 */
	displayCookieOnFrontend() {

		// Retrieve the cookie data.
		let cookie = this.get( false );

		// Select the user information container element.
		const userInfoContainer = document.querySelector( '.user-fpc-info' );

		// If the container is not found, exit the function.
		if ( ! userInfoContainer ) {
			return;
		}

		userInfoContainer.innerHTML = ''; // Clear previous content

		// Create and display a heading indicating no user data is available.
		const noDataHeading = document.createElement( 'h2' );
		noDataHeading.textContent = 'No User data available.';

		if ( ! cookie ) {
			userInfoContainer.appendChild( noDataHeading );
			return;
		}

		// Check if cookie is empty or if 'gFormData' and 'emal' are empty.
		if ( ( ! cookie.gFormData || 0 === Object.keys( cookie.gFormData ).length ) && ! cookie.emal ) {
			userInfoContainer.appendChild( noDataHeading );
			return;
		}

		const gFormData = cookie.gFormData;

		// Create and display a heading indicating user information.
		const heading = document.createElement( 'h2' );
		heading.textContent = 'User Information';

		// Create a container for the table.
		const tableContainer = document.createElement( 'div' );
		tableContainer.classList.add( 'table-container' );

		// Filter out empty values from gFormData.
		const entries = Object.entries( gFormData ).filter( ( [ key, value ] ) => value );

		// Create table elements.
		const table = document.createElement( 'table' );
		const thead = document.createElement( 'thead' );
		const tbody = document.createElement( 'tbody' );

		// Create and append table headers.
		const headerRow = document.createElement( 'tr' );
		const thKey = document.createElement( 'th' );
		thKey.textContent = 'Key';
		const thValue = document.createElement( 'th' );
		thValue.textContent = 'Value';
		headerRow.appendChild( thKey );
		headerRow.appendChild( thValue );
		thead.appendChild( headerRow );
		table.appendChild( thead );

		// Add table rows for each key-value pair in gFormData.
		entries.forEach( ( [ key, value ] ) => {
			const row = document.createElement( 'tr' );
			const tdKey = document.createElement( 'td' );
			tdKey.classList.add( 'info-key' );
			tdKey.textContent = key;
			const tdValue = document.createElement( 'td' );
			tdValue.textContent = value;
			row.appendChild( tdKey );
			row.appendChild( tdValue );
			tbody.appendChild( row );
		} );

		table.appendChild( tbody );
		tableContainer.appendChild( table );

		userInfoContainer.appendChild( heading );
		userInfoContainer.appendChild( tableContainer );

		// Decrypt the email and add it to the table.
		Utils.decrypt(
			cookie.emal,
			( data ) => {
				if ( data ) {
					const row = document.createElement( 'tr' );
					const tdKey = document.createElement( 'td' );
					tdKey.textContent = 'Email Address';
					const tdValue = document.createElement( 'td' );
					tdValue.textContent = data;
					row.appendChild( tdKey );
					row.appendChild( tdValue );
					tbody.appendChild( row );
				}
			},
			true
		);
	},

	/**
	 * Updates the URL of a button with class 'fpc-button' by adding query parameters
	 * extracted from a cookie named 'coxmkt'.
	 *
	 * @returns {void}
	 */
	updateFpcButtonUrl() {

		// Find the button with class 'fpc-button'
		const button = document.querySelector( '.fpc-button' );

		if ( ! button ) {
			console.info( 'No TPC button found.' );
			return;
		}

		// Get the button's URL
		let url = new URL( button.href );

		// Get the cookie value
		let cookie = this.get( false );

		if ( ! cookie ) {
			console.info( 'Cookie not found.' );
			button.classList.remove( 'disabled' );
			return;
		}

		// Extract the required values from the cookie
		const gFormData = cookie.gFormData || {};
		const params = {
			email: cookie.emal,
			firstName: gFormData.firstname,
			lastName: gFormData.lastname
		};

		// Filter out empty values
		const entries = Object.entries( params ).filter( ( [ , value ] ) => value );

		if ( 0 === entries.length ) {
			console.info( 'No user info found for TPC button.' );
			button.classList.remove( 'disabled' );
			return;
		}

		// Update the URL with query parameters
		entries.forEach( ( [ key, value ] ) => url.searchParams.set( key, value ) );

		// Handle async decryption of email
		if ( params.email ) {
			Utils.decrypt(
				params.email,
				( data ) => {
					if ( data ) {
						url.searchParams.set( 'email', data );

						// Set the updated URL back to the button
						button.href = url.toString();
						button.classList.remove( 'disabled' );
					} else {
						console.info( 'Failed to decrypt email.' );
					}
				},
				true
			);
		} else {

			// Set the updated URL back to the button if no email to decrypt
			button.href = url.toString();
			button.classList.remove( 'disabled' );
		}
	}
};

export default GlobalCookie;
